import { useMemo, useState, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { ControllerRenderProps, useFormContext } from 'react-hook-form'
import { BetriebsstelleField, Input } from '../../form'
import {
  AuftragOption,
  AuftragOptionen,
  BefehlFormAuftrag,
  BefehlFormField
} from '../Generic'
import { Betriebsstellenart } from '../../../types'
import { DEFAULT_AUFTRAG_25_20 } from './schema/befehl25-form.schema'
import MarkAuftraegeAsDoneContext from '../../../contexts/MarkAuftraegeAsDoneContext'
import SlideToUnlock from '../../SlideToUnlock'

export interface Auftrag25_20FormProps {
  befehlKennung: string
  fieldPrefix: string
  readOnly: boolean
  disabled?: boolean
  displaySlideToUnlock: boolean
  'data-testid': string
  activateEmptyFieldWarnings: boolean
  noIndent?: boolean
}

export default function Auftrag25_20Form({
  readOnly,
  fieldPrefix,
  'data-testid': testId,
  displaySlideToUnlock,
  activateEmptyFieldWarnings = false,
  noIndent = true
}: Readonly<Auftrag25_20FormProps>) {
  const formContext = useFormContext()
  const { control, formState, watch, resetField } = formContext
  const { errors } = formState

  const { t } = useTranslation('ui-befehle')

  const { enabled } = watch(`${fieldPrefix}`)

  const fieldErrors = useMemo(() => {
    return errors ?? formState.errors
  }, [errors, formState.errors])

  const context = useContext(MarkAuftraegeAsDoneContext)
  const befehlFrontendId = context?.befehlFrontendId
  const isQuittiert = context?.isBefehlsnachrichtQuittiert

  const [confirmed, setConfirmed] = useState<boolean>(
    context?.isDone(befehlFrontendId + '-25_20') || false
  )

  const updateSlideToUnlockState = (value: boolean) => {
    context?.setMarkedAsDone(befehlFrontendId + '-25_20', value)
    setConfirmed(value)
  }

  if (!readOnly || enabled) {
    return (
      <div className={noIndent ? 'relative' : 'relative pl-4'}>
        {confirmed && isQuittiert && (
          <div
            className="absolute z-30 inset-0 bg-white/50 dark:bg-cool-gray-800/65 pointer-events-none"
            data-testid={`${testId}-gray-out-overlay`}
          />
        )}
        <BefehlFormAuftrag
          auftragKennung="25.20"
          fieldName="auftrag_25_20.enabled"
          testId={testId}
          readOnly={readOnly}
          onChange={(checked) => {
            if (!checked) {
              resetField('auftrag_25_20', {
                defaultValue: DEFAULT_AUFTRAG_25_20
              })
            }
          }}
        >
          <div
            className={
              'flex flex-col flex-wrap items-baseline sm:flex-row w-full gap-0.5'
            }
          >
            <span data-testid={`${testId}-text`}>
              {t('auftraege.25_20.label')}
            </span>
            <BefehlFormField
              name={`${fieldPrefix}.km`}
              label={t('auftraege.25_20.km')}
              control={control}
              readOnly={readOnly}
              activateEmptyWarning={activateEmptyFieldWarnings}
              useTextDisplayInReadOnly={false}
              data-testid={`${testId}-feld-25-21`}
              render={({ field: field_25_21 }) => (
                <div>
                  <Input
                    {...field_25_21}
                    label={t('auftraege.25_20.km')}
                    className="w-full"
                    disabled={!enabled}
                    readOnly={readOnly}
                    activateEmptyWarning={activateEmptyFieldWarnings}
                    data-testid={`${testId}-feld-25-21-inner`}
                    errors={fieldErrors}
                  />
                  <span
                    className={enabled ? '' : 'text-gray-500 opacity-60'}
                    data-testid={`${testId}-feld-25-21-label`}
                  >
                    {t('auftraege.25_20.km_label')}
                  </span>
                </div>
              )}
            />
            <span data-testid={`${testId}-text-auf-der`}>
              {t('auftraege.25_20.auf_der')}
            </span>

            <BefehlFormField
              name={`${fieldPrefix}.selector`}
              control={control}
              label=""
              readOnly={readOnly}
              activateEmptyWarning={activateEmptyFieldWarnings}
              useTextDisplayInReadOnly={false}
              data-testid={`${testId}-feld-25-22-23-selector`}
              render={({ field }: { field: ControllerRenderProps }) => (
                <AuftragOptionen
                  name={`${fieldPrefix}.selector`}
                  value={field.value}
                  onChange={field.onChange}
                  disabled={!enabled}
                  readOnly={readOnly}
                  errors={fieldErrors}
                  data-testid={`${testId}-feld-25-22-23-selector-options`}
                >
                  <AuftragOption
                    value={Betriebsstellenart.Abzw}
                    label={t('auftraege.25_20.abzw_label')}
                    data-testid={`${testId}-feld-25-22-23-selector-options-abzw`}
                    disabled={!enabled}
                  >
                    <BefehlFormField
                      name={`${fieldPrefix}.name`}
                      label={t('auftraege.25_20.abzw')}
                      control={control}
                      readOnly={readOnly}
                      activateEmptyWarning={activateEmptyFieldWarnings}
                      data-testid={`${testId}-feld-25-22`}
                      render={({ field }) => (
                        <BetriebsstelleField
                          {...field}
                          label={t('auftraege.25_20.abzw')}
                          variant="solid"
                          className="w-full"
                          disabled={!enabled}
                          data-testid={`${testId}-feld-25-22-inner`}
                          errors={fieldErrors}
                          typ={Betriebsstellenart.Abzw}
                        />
                      )}
                    />
                  </AuftragOption>
                  <AuftragOption
                    value={Betriebsstellenart.Uest}
                    label={t('auftraege.25_20.uest_label')}
                    data-testid={`${testId}-feld-25-22-23-selector-options-uest`}
                    disabled={!enabled}
                  >
                    <BefehlFormField
                      name={`${fieldPrefix}.name`}
                      label={t('auftraege.25_20.uest')}
                      control={control}
                      readOnly={readOnly}
                      activateEmptyWarning={activateEmptyFieldWarnings}
                      data-testid={`${testId}-feld-25-23`}
                      render={({ field }) => (
                        <BetriebsstelleField
                          {...field}
                          label={t('auftraege.25_20.uest')}
                          variant="solid"
                          className="w-full"
                          disabled={!enabled}
                          data-testid={`${testId}-feld-25-23-inner`}
                          errors={errors}
                          typ={Betriebsstellenart.Uest}
                        />
                      )}
                    />
                  </AuftragOption>
                </AuftragOptionen>
              )}
            />

            <span data-testid={`${testId}-text-weiterfahren`}>
              {t('auftraege.25_20.weiterfahren')}
            </span>
          </div>
        </BefehlFormAuftrag>
        {context && displaySlideToUnlock && (
          <SlideToUnlock
            className="pt-0.75 sm:pl-5 sm:pr-5"
            data-testid={`${testId}-abgearbeitet-markieren-slideToUnlock`}
            confirmed={confirmed}
            onConfirmedChange={updateSlideToUnlockState}
            variant="secondary-solid"
          />
        )}
      </div>
    )
  }
}
